import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { mlmAPI } from '../../services/api';
import { ChevronRight, ChevronDown, User, Trophy } from 'lucide-react';

const TreeNode = ({ node, isDarkMode }) => {
  const [expanded, setExpanded] = useState(node.level < 2);

  if (!node) return null;
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="select-none">
      <div
        className={`flex items-center gap-2 py-1.5 px-2 rounded-lg cursor-pointer hover:${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} transition-colors`}
        style={{ paddingLeft: `${node.level * 24 + 8}px` }}
        onClick={() => setExpanded(!expanded)}
      >
        {hasChildren ? (
          expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />
        ) : (
          <div className="w-4" />
        )}
        <User className={`w-4 h-4 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
        <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
          {node.name || 'Unknown'}
        </span>
        {node.level !== undefined && (
          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Lv {node.level}
          </span>
        )}
        {node.rank && (
          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gradient-to-r from-orange-500/10 to-orange-600/10 text-orange-600 ${isDarkMode ? 'text-orange-400' : ''}`}>
            <Trophy className="w-3 h-3" /> {node.rank}
          </span>
        )}
      </div>
      {expanded && hasChildren && (
        <div className="ml-2 border-l-2 border-dashed border-gray-300 dark:border-gray-600">
          {node.children.map((child, i) => (
            <TreeNode key={child.id || i} node={child} isDarkMode={isDarkMode} />
          ))}
        </div>
      )}
    </div>
  );
};

const UnilevelTree = () => {
  const { isDarkMode } = useTheme();
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    mlmAPI.getUnilevelTree(5)
      .then((res) => setTree(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const cardClass = `${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div>
        <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Unilevel Tree</h1>
        <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Your unilevel downline structure</p>
      </div>

      {tree?.upline && (
        <div className={cardClass}>
          <h2 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Sponsor / Upline</h2>
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-2xl bg-gradient-to-r from-orange-500 to-orange-600 text-white`}>
              <User className="w-6 h-6" />
            </div>
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{tree.upline.name || 'N/A'}</p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{tree.upline.sttora_id || ''}</p>
            </div>
          </div>
        </div>
      )}

      {tree?.tree?.children && tree.tree.children.length > 0 ? (
        <div className={cardClass}>
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Downline</h2>
          <div className={`border rounded-xl p-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            {tree.tree.children.map((node, i) => (
              <TreeNode key={node.id || i} node={node} isDarkMode={isDarkMode} />
            ))}
          </div>
        </div>
      ) : (
        <div className={cardClass}>
          <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No downline data available
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default UnilevelTree;
